<script>
    import { onMount } from 'svelte';
    import { getApiBase } from './apiBase.js';
    import TeacherFinderDisplayComponent from './TeacherFinderDisplayComponent.svelte';
    import Autocomplete from './Autocomplete.svelte';
    import VisualTimeRangePicker from './VisualTimeRangePicker.svelte';
    import { SUBJECT_NAMES } from './subjectCatalog.js';

    const apiBase = getApiBase();
    const fallbackSubjects = SUBJECT_NAMES;
    const grades = Array.from({ length: 13 }, (_, index) => index + 1);

    let subjects = fallbackSubjects;
    let selectedSubject = '';
    let selectedGrade = 6;
    let timeSlots = [];
    let responseData = null;
    let submittedTimeSlots = [];
    let loading = false;
    let errorMessage = '';

    onMount(loadSubjects);

    async function loadSubjects() {
        try {
            const response = await fetch(`${apiBase}/studentplayground/teacher-finder/`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.subjects?.length) subjects = data.subjects;
            }
        } catch (error) {
            console.warn('Could not load teacher-finder subjects; using fallback list.', error);
        }
    }

    function addTimeSlot() {
        timeSlots = [...timeSlots, { start_time: '', end_time: '', day_of_week: '1' }];
    }

    function removeTimeSlot(index) {
        timeSlots = timeSlots.filter((_, slotIndex) => slotIndex !== index);
    }

    function updateTimeSlot(index, key, value) {
        timeSlots = timeSlots.map((slot, slotIndex) => (
            slotIndex === index ? { ...slot, [key]: value } : slot
        ));
    }

    function validateForm() {
        if (!selectedSubject.trim()) return 'Choose a subject.';
        for (const [index, slot] of timeSlots.entries()) {
            if (!slot.start_time || !slot.end_time) return `Complete or remove preferred time ${index + 1}.`;
            if (slot.start_time >= slot.end_time) return `Preferred time ${index + 1} must end after it starts.`;
        }
        return '';
    }

    async function submitForm() {
        errorMessage = validateForm();
        if (errorMessage) return;
        loading = true;
        responseData = null;
        submittedTimeSlots = timeSlots.map(slot => ({ ...slot }));
        try {
            const response = await fetch(`${apiBase}/studentplayground/teacher-finder/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ subject: selectedSubject, grade: selectedGrade, time_slots: submittedTimeSlots })
            });
            const data = await response.json();
            if (response.status === 401) {
                localStorage.removeItem('jwtToken');
                throw new Error('Your local session has expired. Sign in again, then return to the teacher finder.');
            }
            if (!response.ok) throw new Error(data.error || 'Teacher search failed.');
            responseData = data;
        } catch (error) {
            errorMessage = error.message || 'Teacher search failed. Please try again.';
        } finally {
            loading = false;
        }
    }
</script>

<section class="finder" aria-labelledby="teacher-finder-title">
    <div class="heading">
        <h3 id="teacher-finder-title">Teacher finder</h3>
        <p>Choose a subject and grade to see every matching teacher and their upcoming weekly availability.</p>
    </div>

    <div class="criteria">
        <div class="form-group">
            <Autocomplete label="Subject" options={subjects} bind:value={selectedSubject} placeholder="Type to search subjects..." />
        </div>
        <div class="form-group">
            <label for="teacher-finder-grade">Grade</label>
            <select id="teacher-finder-grade" bind:value={selectedGrade}>
                {#each grades as grade}<option value={grade}>{grade}</option>{/each}
            </select>
        </div>
    </div>

    <div class="preferences">
        <div class="preferences-heading">
            <div>
                <h4>Preferred times <span>Optional</span></h4>
                <p>Add times to rank teachers by free-time overlap. No matching teacher will be hidden.</p>
            </div>
            <button class="secondary" type="button" on:click={addTimeSlot}>+ Add preferred time</button>
        </div>
        {#each timeSlots as slot, index}
            <div class="time-slot-wrapper">
                <VisualTimeRangePicker
                    startTime={slot.start_time}
                    endTime={slot.end_time}
                    dayOfWeek={slot.day_of_week}
                    on:startTimeChange={(event) => updateTimeSlot(index, 'start_time', event.detail)}
                    on:endTimeChange={(event) => updateTimeSlot(index, 'end_time', event.detail)}
                    on:dayOfWeekChange={(event) => updateTimeSlot(index, 'day_of_week', event.detail)}
                />
                <button class="remove" type="button" on:click={() => removeTimeSlot(index)}>Remove</button>
            </div>
        {/each}
    </div>

    {#if errorMessage}<p class="error" role="alert">{errorMessage}</p>{/if}
    <button class="search" type="button" disabled={loading} on:click={submitForm}>
        {loading ? 'Finding teachers…' : 'Find teachers'}
    </button>

    {#if responseData}
        <TeacherFinderDisplayComponent data={responseData.matched_teachers} searchedTimeSlots={submittedTimeSlots} availabilityWindow={responseData.availability_window} />
    {/if}
</section>

<style>
    .finder { width: 100%; color: #1f2937; }
    .heading h3 { margin: 0 0 .35rem; font-size: 1.5rem; }
    .heading p, .preferences p { margin: 0; color: #5f6b7a; line-height: 1.5; }
    .criteria { display: grid; grid-template-columns: minmax(240px, 2fr) minmax(140px, 1fr); gap: 1.25rem; margin: 1.5rem 0; }
    .form-group { min-width: 0; }
    .form-group > label { display: block; margin-bottom: .45rem; font-weight: 650; }
    select { width: 100%; min-height: 43px; padding: .6rem .75rem; border: 1px solid #cbd5e1; border-radius: 8px; background: white; font-size: 1rem; }
    .preferences { padding: 1rem; border: 1px solid #dbe3ec; border-radius: 12px; background: #f8fafc; }
    .preferences-heading { display: flex; justify-content: space-between; align-items: start; gap: 1rem; }
    .preferences h4 { margin: 0 0 .25rem; }
    .preferences h4 span { margin-left: .35rem; padding: .2rem .5rem; border-radius: 999px; background: #e2e8f0; color: #475569; font-size: .72rem; text-transform: uppercase; }
    .time-slot-wrapper { margin-top: 1rem; }
    button { border: 0; border-radius: 8px; cursor: pointer; font-weight: 650; }
    button:focus-visible, select:focus-visible { outline: 3px solid rgba(37, 99, 235, .35); outline-offset: 2px; }
    .secondary { padding: .7rem 1rem; color: #1d4ed8; background: #dbeafe; white-space: nowrap; }
    .remove { display: block; margin: .4rem 0 0 auto; padding: .55rem .9rem; color: #991b1b; background: #fee2e2; }
    .search { margin-top: 1rem; padding: .8rem 1.4rem; color: #111827; background: #fab45a; font-size: 1rem; }
    .search:hover { background: #f9a742; }
    .search:disabled { cursor: wait; opacity: .65; }
    .error { margin: 1rem 0 0; padding: .75rem 1rem; color: #991b1b; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; }
    @media (max-width: 700px) {
        .criteria { grid-template-columns: 1fr; }
        .preferences-heading { flex-direction: column; }
        .secondary, .search { width: 100%; }
    }
</style>
